import React, { useCallback, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import classNames from "classnames";
import { FormikErrors, FormikProps, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import { FormTextArea } from "@whatslly/ui-components/v2/FormElements/FormTextArea";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { ButtonSet } from "@whatslly/ui-components/v2/BaseElements/ButtonSet";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { AWS_REGION_LIST } from "@whatslly/commons/utils";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { FiltersStore } from "../../../services/filtersStore";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import type { DestinationFormProps, ExtractDestination } from "../utils/DestinationsListTypes";
import { newOidcExternalId, oidcProviderUrl } from "../utils/oidcIdentity";
import styles from "./Modals.module.scss";

// Forms for the org's own secure-storage bucket (integrationType "secureStorage"):
// where Tuvis Browser keeps saved downloads instead of the Tuvis-hosted bucket.
// The listing omits the secret fields (MASK_SETTINGS_FIELDS), so on an edit a blank
// secret means "keep the stored one" — messages-server merges it back in, for the
// test connection too, as long as the payload carries the destination's _id.

type SecureStorageType = "secureStorageS3" | "secureStorageR2" | "secureStorageAzureBlob" | "secureStorageGcs";
type FormValues = Record<string, string | boolean | undefined>;

/** FormSelect stores the whole option object in Formik; send its value. */
function normalized(values: Record<string, unknown>): FormValues {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value && typeof value === "object" && "value" in value ? (value as { value: string }).value : (value as string | boolean | undefined),
    ])
  );
}

const SecureStorageFormCore: React.FC<{
  type: SecureStorageType;
  destinationId?: string;
  initialValues: FormValues;
  /** Fields that must be filled. */
  required: string[];
  /** Secret fields: required unless the stored ones can be kept. */
  secrets: string[];
  keepStoredSecrets: boolean;
  /** Fields of the other auth modes, dropped from what is sent. */
  omit?: string[];
  onSubmitForm: (data: Destination) => void;
  children: React.ReactNode;
}> = observer(({ type, destinationId, initialValues, required, secrets, keepStoredSecrets, omit = [], onSubmitForm, children }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormValues>>(null);
  const requiredFields = useMemo(() => (keepStoredSecrets ? required : [...required, ...secrets]), [keepStoredSecrets, required, secrets]);
  const [hasErrors, setHasErrors] = useState(() => requiredFields.some((f) => !initialValues[f]));

  const validate = useCallback(
    async (raw: FormValues) => {
      const values = normalized(raw);
      const errors: FormikErrors<FormValues> = {};
      for (const field of requiredFields) {
        const value = values[field];
        if (typeof value !== "string" || !value.trim()) errors[field] = t("field_is_required");
      }
      setHasErrors(Object.keys(errors).length > 0);
      return errors;
    },
    [requiredFields, t]
  );

  const getFormValues = useCallback((): Destination => {
    const settings = normalized(formikContext.current?.values ?? initialValues);
    for (const field of omit) delete settings[field];
    for (const field of secrets) if (settings[field] === "") delete settings[field];
    settings.encryptFiles = settings.encryptFiles !== false;
    return { type, integrationType: DESTINATION_INTEGRATION_TYPES.SECURE_STORAGE, settings } as unknown as Destination;
  }, [initialValues, omit, secrets, type]);

  const onSubmit = async () => {
    const data = getFormValues();
    store.testConnection(t, data, destinationId);
    const res = await store.testConnectionPromise;
    if (res?.success) onSubmitForm(data);
  };

  return (
    <FormElement initialValues={initialValues} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <p className={styles.description}>{t("destinations_page.secure_storage.description")}</p>
      {children}
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.secure_storage.prefix")} />
        <FormTextInput
          name="prefix"
          paddingInline="none"
          inputSize="small"
          placeholder={t("destinations_page.secure_storage.prefix_placeholder")}
          data-testid="secure-storage-prefix"
        />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.secure_storage.encrypt_files")} />
        <FormCheckBox name="encryptFiles" paddingInline="none" data-testid="secure-storage-encrypt-files" />
      </div>
      <EncryptionHint />
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={hasErrors} store={store} />
    </FormElement>
  );
});

const EncryptionHint: React.FC = () => {
  const { t } = useTranslation();
  const encrypt = useFormikContext<FormValues>().values.encryptFiles !== false;
  return (
    <p className={styles.description} data-testid="secure-storage-encryption-hint">
      {encrypt ? t("destinations_page.secure_storage.encrypt_files_hint") : t("destinations_page.secure_storage.plain_files_warning")}
    </p>
  );
};

const commonInitialValues = (settings?: { encryptFiles?: boolean; prefix?: string }): FormValues => ({
  prefix: settings?.prefix ?? "",
  encryptFiles: settings?.encryptFiles !== false,
});

const SecretInput: React.FC<{ name: string; keepStored: boolean; multiline?: boolean }> = ({ name, keepStored, multiline }) => {
  const { t } = useTranslation();
  const placeholder = keepStored ? t("destinations_page.secure_storage.keep_secret_placeholder") : undefined;
  if (multiline) return <FormTextArea name={name} paddingInline="none" labelText="" placeholder={placeholder} />;
  return <FormPasswordTextInput name={name} paddingInline="none" inputSize="small" placeholder={placeholder} />;
};

// --- Amazon S3 ---------------------------------------------------------------

type S3AuthMode = "access-key" | "idp";

export const SecureStorageS3ModalForm: React.FC<DestinationFormProps<"secureStorageS3">> = ({ settings, destinationId, onSubmitForm }) => {
  const { t } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const filtersStore = useDependencyInjector(FiltersStore);
  const orgId = filtersStore.orgId || userStore.user?.organizationId;
  const [mode, setMode] = useState<S3AuthMode>(settings?.roleArn ? "idp" : "access-key");

  const externalId = useMemo(() => settings?.externalId ?? newOidcExternalId(orgId), [settings?.externalId, orgId]);
  const providerUrl = oidcProviderUrl(orgId);
  const keepStoredSecrets = Boolean(destinationId && settings?.accessKeyId);

  const initialValues = useMemo<FormValues>(
    () => ({
      bucket: settings?.bucket ?? "",
      region: settings?.region ?? AWS_REGION_LIST[0],
      accessKeyId: settings?.accessKeyId ?? "",
      secretAccessKey: "",
      roleArn: settings?.roleArn ?? "",
      externalId,
      ...commonInitialValues(settings),
    }),
    [settings, externalId]
  );

  const tabs = useMemo(
    () => [
      { key: "access-key" as const, value: t("destinations_page.access_key_auth"), testId: "access-key-tab" },
      { key: "idp" as const, value: t("destinations_page.identity_provider_auth"), testId: "idp-tab" },
    ],
    [t]
  );

  return (
    <>
      <ButtonSet<{ key: S3AuthMode; value: string }>
        testId="switch-connect-mode"
        className={classNames(styles.tabs, styles.s3Tabs)}
        items={tabs}
        onClick={(item) => setMode(item.key)}
        activeItem={tabs.find((tab) => tab.key === mode) ?? tabs[0]}
      />
      <SecureStorageFormCore
        key={mode}
        type="secureStorageS3"
        destinationId={destinationId}
        initialValues={initialValues}
        required={mode === "idp" ? ["bucket", "region", "roleArn", "externalId"] : ["bucket", "region", "accessKeyId"]}
        secrets={mode === "idp" ? [] : ["secretAccessKey"]}
        keepStoredSecrets={keepStoredSecrets}
        omit={mode === "idp" ? ["accessKeyId", "secretAccessKey"] : ["roleArn", "externalId"]}
        onSubmitForm={onSubmitForm as (data: Destination) => void}
      >
        {mode === "idp" && (
          <p className={styles.description}>
            {t("destinations_page.openid_provider_url")}: <br />
            <a href={providerUrl} target="_blank" rel="noreferrer">
              {providerUrl}
            </a>
          </p>
        )}
        <div className={styles.fieldGroup}>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
            <FormTextInput name="bucket" paddingInline="none" inputSize="small" />
          </div>
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.region")} required />
            <FormSelect<{ label: string; value: string }>
              selectSize="small"
              ignoreMenuDirection={true}
              isSearchable={true}
              name="region"
              paddingInline="none"
              options={AWS_REGION_LIST.map((opt) => ({ label: opt, value: opt }))}
              defaultValue={{ label: initialValues.region as string, value: initialValues.region as string }}
              isClearable={false}
              required
            />
          </div>
        </div>
        {mode === "idp" ? (
          <>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.iam_role_name")} required />
              <FormTextInput name="roleArn" paddingInline="none" inputSize="small" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.external_id")} />
              <FormTextInput name="externalId" paddingInline="none" disabled inputSize="small" />
            </div>
          </>
        ) : (
          <>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.accesses_key_id")} required />
              <FormTextInput name="accessKeyId" paddingInline="none" inputSize="small" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.secret_access_key")} required={!keepStoredSecrets} />
              <SecretInput name="secretAccessKey" keepStored={keepStoredSecrets} />
            </div>
          </>
        )}
      </SecureStorageFormCore>
    </>
  );
};

// --- Cloudflare R2 -----------------------------------------------------------

export const SecureStorageR2ModalForm: React.FC<DestinationFormProps<"secureStorageR2">> = ({ settings, destinationId, onSubmitForm }) => {
  const { t } = useTranslation();
  const keepStoredSecrets = Boolean(destinationId && settings);
  const initialValues = useMemo<FormValues>(
    () => ({
      bucket: settings?.bucket ?? "",
      accountId: settings?.accountId ?? "",
      accessKeyId: settings?.accessKeyId ?? "",
      secretAccessKey: "",
      ...commonInitialValues(settings),
    }),
    [settings]
  );
  return (
    <SecureStorageFormCore
      type="secureStorageR2"
      destinationId={destinationId}
      initialValues={initialValues}
      required={["bucket", "accountId", "accessKeyId"]}
      secrets={["secretAccessKey"]}
      keepStoredSecrets={keepStoredSecrets}
      onSubmitForm={onSubmitForm as (data: Destination) => void}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
        <FormTextInput name="bucket" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.secure_storage.r2_account_id")} required />
        <FormTextInput name="accountId" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.accesses_key_id")} required />
        <FormTextInput name="accessKeyId" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.secret_access_key")} required={!keepStoredSecrets} />
        <SecretInput name="secretAccessKey" keepStored={keepStoredSecrets} />
      </div>
    </SecureStorageFormCore>
  );
};

// --- Azure Blob Storage ------------------------------------------------------

type AzureCredentialType = "storage-shared-key-credential" | "client-secret-credential" | "sas-url-credential";

const AZURE_FIELDS: Record<AzureCredentialType, { required: string[]; secrets: string[] }> = {
  "storage-shared-key-credential": { required: ["accountName", "containerName"], secrets: ["accountKey"] },
  "client-secret-credential": { required: ["url", "tenantId", "clientId", "containerName"], secrets: ["clientSecret"] },
  "sas-url-credential": { required: [], secrets: ["sasUrl"] },
};

export const SecureStorageAzureBlobModalForm: React.FC<DestinationFormProps<"secureStorageAzureBlob">> = ({
  settings,
  destinationId,
  onSubmitForm,
}) => {
  const { t } = useTranslation();
  const [credentialType, setCredentialType] = useState<AzureCredentialType>(settings?.type ?? "storage-shared-key-credential");
  // messages-server only refills secrets of the same credential type.
  const keepStoredSecrets = Boolean(destinationId && settings?.type === credentialType);
  const stored = (settings ?? {}) as Record<string, string | undefined>;

  const initialValues = useMemo<FormValues>(
    () => ({
      type: credentialType,
      accountName: stored.accountName ?? "",
      containerName: stored.containerName ?? "",
      url: stored.url ?? "",
      tenantId: stored.tenantId ?? "",
      clientId: stored.clientId ?? "",
      accountKey: "",
      clientSecret: "",
      sasUrl: "",
      ...commonInitialValues(settings),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settings, credentialType]
  );
  const otherFields = (Object.keys(AZURE_FIELDS) as AzureCredentialType[])
    .filter((type) => type !== credentialType)
    .flatMap((type) => [...AZURE_FIELDS[type].required, ...AZURE_FIELDS[type].secrets])
    .filter((field) => ![...AZURE_FIELDS[credentialType].required, ...AZURE_FIELDS[credentialType].secrets].includes(field));

  const tabs = useMemo(
    () => [
      { key: "storage-shared-key-credential" as const, value: "Storage Shared Key", testId: "storage-shared-key-credential-tab" },
      { key: "client-secret-credential" as const, value: "Client Secret", testId: "client-secret-credential-tab" },
      { key: "sas-url-credential" as const, value: "SAS URL", testId: "sas-url-credential-tab" },
    ],
    []
  );

  return (
    <>
      <ButtonSet<{ key: AzureCredentialType; value: string }>
        testId="switch-azure-blob-mode"
        className={classNames(styles.tabs, styles.azureBlobTabs)}
        items={tabs}
        onClick={(item) => setCredentialType(item.key)}
        activeItem={tabs.find((tab) => tab.key === credentialType) ?? tabs[0]}
      />
      <SecureStorageFormCore
        key={credentialType}
        type="secureStorageAzureBlob"
        destinationId={destinationId}
        initialValues={initialValues}
        required={AZURE_FIELDS[credentialType].required}
        secrets={AZURE_FIELDS[credentialType].secrets}
        keepStoredSecrets={keepStoredSecrets}
        omit={otherFields}
        onSubmitForm={onSubmitForm as (data: Destination) => void}
      >
        {credentialType === "storage-shared-key-credential" && (
          <>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.account_name")} required />
              <FormTextInput name="accountName" paddingInline="none" inputSize="small" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.account_key")} required={!keepStoredSecrets} />
              <SecretInput name="accountKey" keepStored={keepStoredSecrets} />
            </div>
          </>
        )}
        {credentialType === "client-secret-credential" && (
          <>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.url")} required />
              <FormTextInput name="url" paddingInline="none" inputSize="small" placeholder="https://<account>.blob.core.windows.net" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.tenant_id")} required />
              <FormTextInput name="tenantId" paddingInline="none" inputSize="small" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.client_id")} required />
              <FormTextInput name="clientId" paddingInline="none" inputSize="small" />
            </div>
            <div className={styles.field}>
              <Label className={styles.filedLabel} text={t("destinations_page.client_secret")} required={!keepStoredSecrets} />
              <SecretInput name="clientSecret" keepStored={keepStoredSecrets} />
            </div>
          </>
        )}
        {credentialType !== "sas-url-credential" && (
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.container_name")} required />
            <FormTextInput name="containerName" paddingInline="none" inputSize="small" />
          </div>
        )}
        {credentialType === "sas-url-credential" && (
          <div className={styles.field}>
            <Label className={styles.filedLabel} text={t("destinations_page.secure_storage.container_sas_url")} required={!keepStoredSecrets} />
            <SecretInput name="sasUrl" keepStored={keepStoredSecrets} />
          </div>
        )}
      </SecureStorageFormCore>
    </>
  );
};

// --- Internal Amazon S3 -------------------------------------------------------
// Tuvis-hosted bucket in the org's pod region, always encrypted. No credentials: a super
// admin just connects it (DestinationsList gates the card on isSuperAdmin).

export const SecureStorageInternalS3ModalForm: React.FC<DestinationFormProps<"secureStorageInternalS3">> = ({ destinationId, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormValues>>(null);

  const onSubmit = async () => {
    const data = {
      type: "secureStorageInternalS3" as const,
      integrationType: DESTINATION_INTEGRATION_TYPES.SECURE_STORAGE,
      settings: {},
    } as ExtractDestination<"secureStorageInternalS3">;
    store.testConnection(t, data as unknown as Destination, destinationId);
    const res = await store.testConnectionPromise;
    if (res?.success) onSubmitForm(data);
  };

  return (
    <FormElement initialValues={{}} onSubmit={onSubmit} formikRef={formikContext}>
      <p className={styles.description}>{t("destinations_page.secure_storage.internal_description")}</p>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={false} store={store} />
    </FormElement>
  );
};

// --- Google Cloud Storage ----------------------------------------------------

export const SecureStorageGcsModalForm: React.FC<DestinationFormProps<"secureStorageGcs">> = ({ settings, destinationId, onSubmitForm }) => {
  const { t } = useTranslation();
  const keepStoredSecrets = Boolean(destinationId && settings);
  const initialValues = useMemo<FormValues>(
    () => ({
      bucket: settings?.bucket ?? "",
      projectId: settings?.projectId ?? "",
      client_email: settings?.client_email ?? "",
      private_key: "",
      ...commonInitialValues(settings),
    }),
    [settings]
  );
  return (
    <SecureStorageFormCore
      type="secureStorageGcs"
      destinationId={destinationId}
      initialValues={initialValues}
      required={["bucket", "client_email"]}
      secrets={["private_key"]}
      keepStoredSecrets={keepStoredSecrets}
      onSubmitForm={onSubmitForm as (data: Destination) => void}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
        <FormTextInput name="bucket" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.project_id")} />
        <FormTextInput name="projectId" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_email")} required />
        <FormTextInput name="client_email" paddingInline="none" inputSize="small" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.private_key")} required={!keepStoredSecrets} />
        <SecretInput name="private_key" keepStored={keepStoredSecrets} multiline />
      </div>
    </SecureStorageFormCore>
  );
};
